import { createAsyncThunk, createSlice, Draft, PayloadAction, SliceCaseReducers } from '@reduxjs/toolkit';
import {
  createDefaultFetch,
  createFetchedSuccess,
  createFetchedWithError,
  createFetchInProgress,
} from 'types/rest/IFetchStatus';
import { convertSerializedErrorToError } from './convertSerializedErrorToError';
import { IFetchDataState } from './IFetchDataState';

export const createAsyncSliceWithDataAndQuery = <Query, Data>(
  name: string,
  action: (x: Query) => Promise<Data>,
  defaultData: Data = {} as Data,
  queryValue: (x: Query) => Partial<Query> = (t) => t
) => {
  const asyncAction = createAsyncThunk(`${name}/fetchData`, action);
  const initialState = {
    data: defaultData,
    fetchError: undefined,
    fetchStatus: createDefaultFetch(),
    query: {},
  } as IFetchDataState<Query, Data>;
  const slice = createSlice<IFetchDataState<Query, Data>, SliceCaseReducers<IFetchDataState<Query, Data>>>({
    name,
    initialState,
    reducers: {
      reset: () => initialState,
      setData: (state, action: PayloadAction<Data>) => {
        state.data = action.payload as Draft<Data>;
        state.fetchError = undefined;
        state.fetchStatus = createFetchedSuccess();
      },
    },
    extraReducers: (builder) =>
      builder
        .addCase(asyncAction.pending, (state, action) => {
          state.query = queryValue(action.meta.arg) as Draft<Query>;
          state.fetchStatus = createFetchInProgress();
        })
        .addCase(asyncAction.fulfilled, (state, action: PayloadAction<Data>) => {
          state.data = action.payload as Draft<Data>;
          state.fetchError = undefined;
          state.fetchStatus = createFetchedSuccess();
        })
        .addCase(asyncAction.rejected, (state, action) => {
          state.fetchError = convertSerializedErrorToError(action.error);
          state.fetchStatus = createFetchedWithError();
        }),
  });
  return { slice, asyncAction };
};
